import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useParams, useNavigate } from 'react-router-dom';
import { colors, typography } from '../theme/tokens';
import { TitleLarge } from '../components/Typography';
import Button from '../components/Button';
import { ArrowLeft, TrashSimple, Plus } from '@phosphor-icons/react';
import { supabase } from '../config/supabase';
import { useToast } from '../contexts/ToastContext';
import { MealPlan, MealPlanWeek, MealPlanDay, MealPlanMeal, MealType } from '../types/mealPlan';
import noImage from '../assets/no-image.png';
// @ts-ignore
import confetti from 'canvas-confetti';
import MealItem from '../components/MealItem';
import RecipePickerDrawer from '../components/RecipePickerDrawer';
import { Modal } from '../components/Modal';

const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const HeaderTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 0.5rem;
`;

const TabContainer = styled.div`
  margin-bottom: 1rem;
`;

const TabList = styled.div`
  display: flex;
  background-color: ${colors.neutral.white};
  border-radius: 999px;
  padding: 0.5rem;
  flex-shrink: 0;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? colors.brand.laurel : colors.neutral.white};
  color: ${props => props.active ? colors.neutral.white : colors.brand.laurel};
  border: none;
  padding: 1rem 2rem;
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  font-weight: 600;
  border-radius: 999px;
  margin-bottom: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(23, 117, 8, 0.08)' : 'none'};
  outline: none;

  &:hover {
    background: ${colors.brand.laurel};
    color: ${colors.neutral.white};
    outline: none;
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:active {
    /* No additional effect on click */
    background: ${props => props.active ? colors.brand.laurel : colors.neutral.white};
    color: ${props => props.active ? colors.neutral.white : colors.brand.laurel};
    outline: none;
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const DetailsContainer = styled.div`
  padding: 2rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${colors.neutral.offWhite};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
  color: ${colors.neutral.darkCharcoal};
  font-weight: 600;
  min-width: 200px;
  flex-shrink: 0;
`;

const DetailValue = styled.div`
  font-family: ${typography.paragraph.medium.fontFamily};
  font-size: ${typography.paragraph.medium.fontSize};
  color: ${colors.neutral.charcoal};
  line-height: 1.5;
  flex: 1;
`;

const CoverImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 8px;
  object-fit: cover;
`;

const KanbanContainer = styled.div`
  overflow-x: auto;
  padding-bottom: 1rem;
`;

const KanbanBoard = styled.div`
  display: flex;
  gap: 1rem;
  min-width: max-content;
`;

const WeekColumn = styled.div`
  min-width: 320px;
  max-width: 320px;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  padding: 1rem;
  @media (max-width: 768px) {
    min-width: 300px;
    max-width: 300px;
  }
`;

const WeekHeader = styled.div`
  color: ${colors.neutral.black};
  padding: 0.75rem;
  border-radius: 6px;
  text-align: left;
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WeekTitle = styled.span``;

const DeleteWeekButton = styled.button`
  background: none;
  border: none;
  color: ${colors.reds.bright};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${colors.reds.muted};
  }
`;

const DayCard = styled.div`
  background-color: ${colors.neutral.white};
  border-radius: 4px;
  padding: .5rem;
  margin-bottom: 0.5rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
`;

const DayName = styled.div`
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
  color: ${colors.neutral.darkCharcoal};
  font-weight: 600;
`;

const MealList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-transform: capitalize;
`;


const SkeletonContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 1rem;
`;

const SkeletonColumn = styled.div`
  min-width: 280px;
  max-width: 280px;
  background-color: ${colors.neutral.offWhite};
  border-radius: 8px;
  padding: 1rem;

  @media (max-width: 768px) {
    min-width: 260px;
    max-width: 260px;
  }
`;

const SkeletonHeader = styled.div`
  height: 40px;
  background-color: ${colors.neutral.lightCharcoal};
  border-radius: 6px;
  margin-bottom: 1rem;
  animation: pulse 1.5s ease-in-out infinite;
`;

const SkeletonCard = styled.div`
  height: 80px;
  background-color: ${colors.neutral.lightCharcoal};
  border-radius: 6px;
  margin-bottom: 0.75rem;
  animation: pulse 1.5s ease-in-out infinite;
`;

const SkeletonPulse = styled.div`
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${colors.neutral.charcoal};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: ${colors.reds.bright};
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  width: 100%;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  color: ${colors.neutral.darkCharcoal};
  padding: 0.5rem 1rem;
  
  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const AddWeekColumn = styled.div`
  min-width: 320px;
  max-width: 320px;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;

  @media (max-width: 768px) {
    min-width: 300px;
    max-width: 300px;
  }
`;

const AddWeekButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  font-size: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.neutral.lightCharcoal};
  border-top-color: ${colors.brand.laurel};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const MealPlanDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [mealPlan, setMealPlan] = useState<MealPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kanbanData, setKanbanData] = useState<{
    weeks: (MealPlanWeek & { days: (MealPlanDay & { meals: MealPlanMeal[] })[] })[]
  } | null>(null);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'meal-plan'>('details');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState<MealPlanMeal | null>(null);
  const [loadingRecipeId, setLoadingRecipeId] = useState<number | null>(null);
  const [deleteWeekModalOpen, setDeleteWeekModalOpen] = useState(false);
  const [weekToDelete, setWeekToDelete] = useState<{ id: string; week_number: number } | null>(null);
  const [isAddingWeek, setIsAddingWeek] = useState(false);

  const fetchKanbanData = async (mealPlanId: string) => {
    setKanbanLoading(true);
    try {
      // Fetch weeks
      const { data: weeks, error: weeksError } = await supabase
        .from('meal_plan_weeks')
        .select('*')
        .eq('meal_plan_id', mealPlanId)
        .order('week_number', { ascending: true });

      if (weeksError) throw weeksError;

      if (!weeks || weeks.length === 0) {
        setKanbanData({ weeks: [] });
        return;
      }

      // Fetch days for all weeks
      const weekIds = weeks.map(w => w.id);
      const { data: days, error: daysError } = await supabase
        .from('meal_plan_days')
        .select('*')
        .in('week_id', weekIds)
        .order('day_of_week', { ascending: true });

      if (daysError) throw daysError;

      // Fetch meals for all days
      const dayIds = days?.map(d => d.id) || [];
      const { data: meals, error: mealsError } = await supabase
        .from('meal_plan_meals')
        .select('*')
        .in('day_id', dayIds)
        .order('meal_type', { ascending: true });

      if (mealsError) throw mealsError;

      // Structure the data
      const structuredWeeks = weeks.map(week => {
        const weekDays = days?.filter(day => day.week_id === week.id) || [];
        const structuredDays = weekDays.map(day => {
          const dayMeals = meals?.filter(meal => meal.day_id === day.id) || [];
          return {
            ...day,
            meals: dayMeals
          };
        });
        return {
          ...week,
          days: structuredDays
        };
      });

      setKanbanData({ weeks: structuredWeeks });
    } catch (err) {
      console.error('Error fetching kanban data:', err);
      showToast('Failed to load meal plan structure', 'error');
    } finally {
      setKanbanLoading(false);
    }
  };

  useEffect(() => {
    const fetchMealPlan = async () => {
      if (!id) {
        setError('No meal plan ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('meal_plans')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error('Meal plan not found');
        }

        setMealPlan(data);
        // Fetch kanban data after meal plan is loaded
        await fetchKanbanData(data.id);
      } catch (err) {
        console.error('Error fetching meal plan:', err);
        setError(err instanceof Error ? err.message : 'Failed to load meal plan');
        showToast('Failed to load meal plan', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchMealPlan();
  }, [id, showToast]);

  const handlePublishToggle = async () => {
    if (!mealPlan) return;
    
    try {
      const { error } = await supabase
        .from('meal_plans')
        .update({ is_live: !mealPlan.is_live })
        .eq('id', mealPlan.id);

      if (error) throw error;

      // Fetch the updated meal plan data
      const { data: updatedMealPlan, error: fetchError } = await supabase
        .from('meal_plans')
        .select('*')
        .eq('id', mealPlan.id)
        .single();

      if (fetchError) throw fetchError;

      setMealPlan(updatedMealPlan);
      showToast(
        updatedMealPlan.is_live ? 'Meal plan published successfully' : 'Meal plan unpublished successfully',
        'success'
      );
      if (updatedMealPlan.is_live) {
        confetti({
          particleCount: 200,
          spread: 180,
          origin: { y: 0.6 },
          colors: ['#177508', '#F6B83F', '#CFE531', '#E358C2', '#A0EA95', '#0E4805']
        });
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      showToast('Failed to update meal plan status', 'error');
    }
  };

  const handleEdit = () => {
    if (!mealPlan) return;
    navigate(`/meal-plans/create/${mealPlan.id}`);
  };

  const getDayName = (dayOfWeek: number): string => {
    console.debug('getDayName called with:', dayOfWeek, typeof dayOfWeek);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[Number(dayOfWeek)] || 'Unknown';
  };

  const handleBack = () => {
    navigate('/meal-plans');
  };

  const handleMealItemClick = (meal: MealPlanMeal) => {
    setSelectedMeal(meal);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedMeal(null);
  };

  const handleRecipeSelect = async (recipeId: number) => {
    if (!selectedMeal) return;
    setLoadingRecipeId(recipeId);
    try {
      const { error } = await supabase
        .from('meal_plan_meals')
        .update({ recipe_id: recipeId })
        .eq('id', selectedMeal.id);
      if (error) throw error;
      // Refresh only the updated meal in kanbanData
      setKanbanData(prev => {
        if (!prev) return prev;
        return {
          weeks: prev.weeks.map(week => ({
            ...week,
            days: week.days.map(day => ({
              ...day,
              meals: day.meals.map(meal =>
                meal.id === selectedMeal.id ? { ...meal, recipe_id: recipeId } : meal
              )
            }))
          }))
        };
      });
      // Confetti
      confetti({
        particleCount: 120,
        spread: 120,
        origin: { y: 0.7 },
        colors: ['#177508', '#F6B83F', '#CFE531', '#E358C2', '#A0EA95', '#0E4805']
      });
      handleDrawerClose();
    } catch (err) {
      showToast('Failed to assign recipe', 'error');
    } finally {
      setLoadingRecipeId(null);
    }
  };

  const handleDeleteWeek = async () => {
    if (!weekToDelete || !mealPlan) return;
    
    try {
      // First, get all days for this week
      const { data: days, error: daysFetchError } = await supabase
        .from('meal_plan_days')
        .select('id')
        .eq('week_id', weekToDelete.id);
      
      if (daysFetchError) throw daysFetchError;
      
      // Delete all meals for these days first
      if (days && days.length > 0) {
        const dayIds = days.map(day => day.id);
        const { error: mealsDeleteError } = await supabase
          .from('meal_plan_meals')
          .delete()
          .in('day_id', dayIds);
        
        if (mealsDeleteError) throw mealsDeleteError;
      }
      
      // Delete all days for this week
      const { error: daysDeleteError } = await supabase
        .from('meal_plan_days')
        .delete()
        .eq('week_id', weekToDelete.id);
      
      if (daysDeleteError) throw daysDeleteError;
      
      // Now delete the week
      const { error: weekDeleteError } = await supabase
        .from('meal_plan_weeks')
        .delete()
        .eq('id', weekToDelete.id);
      
      if (weekDeleteError) throw weekDeleteError;
      
      // Renumber remaining weeks
      const { data: remainingWeeks, error: fetchError } = await supabase
        .from('meal_plan_weeks')
        .select('id, week_number')
        .eq('meal_plan_id', mealPlan.id)
        .gt('week_number', weekToDelete.week_number)
        .order('week_number', { ascending: true });
      
      if (fetchError) throw fetchError;
      
      // Update week numbers for remaining weeks
      if (remainingWeeks && remainingWeeks.length > 0) {
        for (const week of remainingWeeks) {
          const newWeekNumber = week.week_number - 1;
          const { error: updateError } = await supabase
            .from('meal_plan_weeks')
            .update({ week_number: newWeekNumber })
            .eq('id', week.id);
          
          if (updateError) throw updateError;
        }
      }
      
      // Refresh kanban data
      await fetchKanbanData(mealPlan.id);
      
      showToast(`Week ${weekToDelete.week_number} deleted successfully`, 'success');
      setDeleteWeekModalOpen(false);
      setWeekToDelete(null);
      
    } catch (error) {
      console.error('Error deleting week:', error);
      showToast('Failed to delete week', 'error');
    }
  };

  const handleWeekDeleteClick = (week: { id: string; week_number: number }) => {
    setWeekToDelete(week);
    setDeleteWeekModalOpen(true);
  };

  const handleAddWeek = async () => {
    if (!mealPlan || !kanbanData) return;
    
    setIsAddingWeek(true);
    try {
      // Get the next week number
      const nextWeekNumber = Math.max(...kanbanData.weeks.map(w => w.week_number)) + 1;
      
      // Get meal types from the first week (if it exists)
      let mealTypes: MealType[] = [];
      if (kanbanData.weeks.length > 0 && kanbanData.weeks[0].days.length > 0) {
        const firstDayMeals = kanbanData.weeks[0].days[0].meals;
        mealTypes = [...new Set(firstDayMeals.map(m => m.meal_type))];
      }
      
      // Create new week
      const { data: newWeek, error: weekError } = await supabase
        .from('meal_plan_weeks')
        .insert([{
          meal_plan_id: mealPlan.id,
          week_number: nextWeekNumber,
          start_date: null
        }])
        .select()
        .single();
      
      if (weekError) throw weekError;
      if (!newWeek) throw new Error('Failed to create week');
      
      // Create days for the new week
      const daysData = Array.from({ length: 7 }, (_, index) => ({
        week_id: newWeek.id,
        day_of_week: index, // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        date: null
      }));
      
      const { data: newDays, error: daysError } = await supabase
        .from('meal_plan_days')
        .insert(daysData)
        .select();
      
      if (daysError) throw daysError;
      if (!newDays) throw new Error('Failed to create days');
      
      // Create meals for each day and meal type
      if (mealTypes.length > 0 && newDays.length > 0) {
        const mealsData: any[] = [];
        newDays.forEach(day => {
          mealTypes.forEach(mealType => {
            mealsData.push({
              day_id: day.id,
              meal_type: mealType,
              recipe_id: null,
              portions_override: null,
              reminder_time: null
            });
          });
        });
        
        if (mealsData.length > 0) {
          const { error: mealsError } = await supabase
            .from('meal_plan_meals')
            .insert(mealsData);
          
          if (mealsError) throw mealsError;
        }
      }
      
      // Refresh kanban data to show the new week
      await fetchKanbanData(mealPlan.id);
      
      showToast(`Week ${nextWeekNumber} added successfully`, 'success');
      
    } catch (error) {
      console.error('Error adding week:', error);
      showToast('Something went wrong while adding the week. Please try again.', 'error');
    } finally {
      setIsAddingWeek(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <LoadingContainer>Loading meal plan...</LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (error || !mealPlan) {
    return (
      <PageContainer>
        <ContentContainer>
          <ErrorContainer>
            <div>Failed to load meal plan</div>
            <Button variant="outline" onClick={handleBack}>
              Back to Meal Plans
            </Button>
          </ErrorContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ButtonContainer>
        <BackButton onClick={() => navigate('/meal-plans')}>
          <ArrowLeft size={20} />
          Back
        </BackButton>
        <ActionButtons>
          <Button
            variant="outline"
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant={mealPlan.is_live ? "outline" : "filled"}
            color={mealPlan.is_live ? "danger" : "primary"}
            onClick={handlePublishToggle}
          >
            {mealPlan.is_live ? 'Unpublish' : 'Publish'}
          </Button>
        </ActionButtons>
      </ButtonContainer>
      
      <ContentContainer>
        <HeaderTitle style={{ marginBottom: '1rem' }}>{mealPlan.title}</HeaderTitle>
        
        <TabContainer>
          <TabList>
            <TabButton
              active={activeTab === 'details'}
              onClick={() => setActiveTab('details')}
            >
              Details
            </TabButton>
            <TabButton
              active={activeTab === 'meal-plan'}
              onClick={() => setActiveTab('meal-plan')}
            >
              Meal Plan
            </TabButton>
          </TabList>
          
          <TabContent>
            {activeTab === 'details' && (
              <DetailsContainer>
                <DetailsRow>
                  <DetailLabel>Title</DetailLabel>
                  <DetailValue>{mealPlan.title}</DetailValue>
                </DetailsRow>

                {mealPlan.description && (
                  <DetailsRow>
                    <DetailLabel>Description</DetailLabel>
                    <DetailValue>{mealPlan.description}</DetailValue>
                  </DetailsRow>
                )}

                <DetailsRow>
                  <DetailLabel>Cover Image</DetailLabel>
                  <DetailValue>
                    <CoverImage 
                      src={mealPlan.cover_image || noImage} 
                      alt={mealPlan.title}
                    />
                  </DetailValue>
                </DetailsRow>

                <DetailsRow>
                  <DetailLabel>Created</DetailLabel>
                  <DetailValue>
                    {new Date(mealPlan.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </DetailValue>
                </DetailsRow>

                <DetailsRow>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    {mealPlan.is_live ? 'Published' : 'Draft'}
                    {mealPlan.is_default && ' • Default Plan'}
                    {mealPlan.is_shared && ' • Shared'}
                    {mealPlan.archived_at && ' • Archived'}
                  </DetailValue>
                </DetailsRow>
              </DetailsContainer>
            )}
            
            {activeTab === 'meal-plan' && (
              <KanbanContainer>
                {kanbanLoading ? (
                  <SkeletonContainer>
                    <SkeletonPulse />
                    {[1, 2, 3, 4].map((week) => (
                      <SkeletonColumn key={week}>
                        <SkeletonHeader />
                        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                          <SkeletonCard key={day} />
                        ))}
                      </SkeletonColumn>
                    ))}
                  </SkeletonContainer>
                ) : kanbanData && kanbanData.weeks.length > 0 ? (
                  <KanbanBoard>
                    {kanbanData.weeks.map((week) => (
                      <WeekColumn key={week.id}>
                        <WeekHeader>
                          <WeekTitle>Week {week.week_number}</WeekTitle>
                          <DeleteWeekButton onClick={() => handleWeekDeleteClick(week)}>
                            <TrashSimple size={20} />
                          </DeleteWeekButton>
                        </WeekHeader>
                        {[...week.days]
                          .sort((a, b) => {
                            // Place Sunday (0) at the end, otherwise sort ascending
                            if (a.day_of_week === 0 && b.day_of_week !== 0) return 1;
                            if (a.day_of_week !== 0 && b.day_of_week === 0) return -1;
                            return a.day_of_week - b.day_of_week;
                          })
                          .map((day) => {
                            console.debug('Rendering day:', day);
                            return (
                              <DayCard key={day.id}>
                                <DayName>{getDayName(day.day_of_week)}</DayName>
                                <MealList>
                                  {day.meals.map((meal) => (
                                    <div key={meal.id} onClick={() => handleMealItemClick(meal)} style={{ cursor: 'pointer' }}>
                                      <MealItem meal_type={meal.meal_type} recipe_id={meal.recipe_id} />
                                    </div>
                                  ))}
                                </MealList>
                              </DayCard>
                            );
                          })}
                      </WeekColumn>
                    ))}
                    <AddWeekColumn>
                      {isAddingWeek ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                          <LoadingSpinner />
                          <span style={{ color: colors.neutral.charcoal }}>Adding week...</span>
                        </div>
                      ) : (
                        <AddWeekButton onClick={handleAddWeek} disabled={isAddingWeek}>
                          <Plus size={20} />
                          Add Week
                        </AddWeekButton>
                      )}
                    </AddWeekColumn>
                  </KanbanBoard>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: colors.neutral.charcoal }}>
                    No meal plan structure found.
                  </div>
                )}
              </KanbanContainer>
            )}
          </TabContent>
        </TabContainer>
      </ContentContainer>
      <RecipePickerDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        meal={selectedMeal}
        onRecipeSelect={handleRecipeSelect}
        loadingRecipeId={loadingRecipeId}
      />
      <Modal
        isOpen={deleteWeekModalOpen}
        onClose={() => {
          setDeleteWeekModalOpen(false);
          setWeekToDelete(null);
        }}
        title="Remove Week?"
      >
        {mealPlan?.is_live ? (
          <>
            <p>This meal plan is currently published. Deleting a week may disrupt users who are following this plan. You may want to unpublish the plan first.</p>
            <p>This will permanently delete Week {weekToDelete?.week_number} and all associated days and meals. This action cannot be undone.</p>
          </>
        ) : (
          <p>This will permanently delete Week {weekToDelete?.week_number} and all associated days and meals. This action cannot be undone.</p>
        )}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <Button onClick={handleDeleteWeek} color="danger">
            Delete Week
          </Button>
          <Button variant="outline" onClick={() => {
            setDeleteWeekModalOpen(false);
            setWeekToDelete(null);
          }}>
            Cancel
          </Button>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default MealPlanDetails; 